import { useState, useEffect, useCallback } from 'react'
import { useClientAuth } from '../../context/ClientAuthContext'
import { FileText, Download, Clock, CheckCircle, AlertTriangle, Calendar, DollarSign } from 'lucide-react'
import './ClientPortal.css'

export default function ClientInvoices() {
  const { clientApi } = useClientAuth()
  const [invoices, setInvoices] = useState([])
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(null)

  useEffect(() => {
    loadInvoices()
  }, [])

  const loadInvoices = async () => {
    try {
      const [invoicesData, summaryData] = await Promise.all([
        clientApi('/invoices'),
        clientApi('/invoices-summary')
      ])

      if (invoicesData.success) setInvoices(invoicesData.invoices)
      if (summaryData.success) setSummary(summaryData.summary)
    } catch (error) {
      console.error('Failed to load invoices:', error)
    }
    setLoading(false)
  }

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    })
  }

  const formatCurrencyPKR = (amount) => {
    return parseFloat(amount || 0).toLocaleString('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle size={16} className="status-icon paid" />
      case 'pending': return <Clock size={16} className="status-icon pending" />
      case 'overdue': return <AlertTriangle size={16} className="status-icon overdue" />
      case 'partial': return <Clock size={16} className="status-icon partial" />
      default: return <FileText size={16} className="status-icon" />
    }
  }

  const getStatusClass = (status) => {
    switch (status) {
      case 'paid': return 'status-paid'
      case 'pending': return 'status-pending'
      case 'overdue': return 'status-overdue'
      case 'partial': return 'status-partial'
      case 'cancelled': return 'status-cancelled'
      default: return ''
    }
  }

  const downloadPdf = async (invoiceId, invoiceNo) => {
    setDownloading(invoiceId)
    try {
      const response = await clientApi(`/invoices/${invoiceId}/pdf`, {}, true) // true for blob

      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(response)
      const link = document.createElement('a')
      link.href = url
      link.download = `${invoiceNo}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download PDF:', error)
      alert('Failed to download invoice PDF. Please try again.')
    }
    setDownloading(null)
  }

  if (loading) {
    return (
      <div className="client-loading">
        <div className="loading-spinner"></div>
        <p>Loading invoices...</p>
      </div>
    )
  }

  return (
    <div className="client-invoices-page">
      <div className="client-page-header">
        <h1>Invoices</h1>
        <p>View and download your monthly invoices</p>
      </div>

      {/* Invoice Stats */}
      <div className="client-stat-blocks">
        <div className="client-stat-block blue">
          <div className="client-stat-icon">
            <FileText size={22} />
          </div>
          <div className="client-stat-content">
            <div className="client-stat-value">{summary?.total?.count || 0}</div>
            <div className="client-stat-label">Total Invoices</div>
          </div>
        </div>

        <div className="client-stat-block orange">
          <div className="client-stat-icon">
            <Clock size={22} />
          </div>
          <div className="client-stat-content">
            <div className="client-stat-value">{summary?.pending?.count || 0}</div>
            <div className="client-stat-label">Pending</div>
          </div>
        </div>

        <div className="client-stat-block green">
          <div className="client-stat-icon">
            <CheckCircle size={22} />
          </div>
          <div className="client-stat-content">
            <div className="client-stat-value">{summary?.paid?.count || 0}</div>
            <div className="client-stat-label">Paid</div>
          </div>
        </div>

        <div className="client-stat-block red">
          <div className="client-stat-icon">
            <AlertTriangle size={22} />
          </div>
          <div className="client-stat-content">
            <div className="client-stat-value">{summary?.overdue?.count || 0}</div>
            <div className="client-stat-label">Overdue</div>
          </div>
        </div>
      </div>

      {/* Invoices List */}
      <div className="client-section">
        <div className="client-table-container">
          <div className="client-table-header">
            <h2>All Invoices</h2>
          </div>

          <table className="client-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Issue Date</th>
                <th>Due Date</th>
                <th>Period</th>
                <th>Bots</th>
                <th>Amount (USD)</th>
                <th>Amount (PKR)</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    No invoices yet
                  </td>
                </tr>
              ) : (
                invoices.map(invoice => (
                  <tr key={invoice.id}>
                    <td className="invoice-no">{invoice.invoice_no}</td>
                    <td>{formatDate(invoice.issue_date)}</td>
                    <td>{formatDate(invoice.due_date)}</td>
                    <td className="period-cell">
                      <span className="period-range">
                        {formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}
                      </span>
                    </td>
                    <td className="text-center">{invoice.total_bots}</td>
                    <td className="amount-cell">{formatCurrency(invoice.subtotal_usd)}</td>
                    <td className="amount-cell pkr">{formatCurrencyPKR(invoice.total_pkr)}</td>
                    <td>
                      <span className={`invoice-status ${getStatusClass(invoice.status)}`}>
                        {getStatusIcon(invoice.status)}
                        {invoice.status}
                      </span>
                    </td>
                    <td>
                      <button
                        className="download-btn"
                        onClick={() => downloadPdf(invoice.id, invoice.invoice_no)}
                        disabled={downloading === invoice.id}
                        title="Download PDF"
                      >
                        {downloading === invoice.id ? (
                          <span className="btn-spinner"></span>
                        ) : (
                          <Download size={16} />
                        )}
                        <span>PDF</span>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        .client-invoices-page {
          animation: fadeIn 0.3s ease;
        }

        .invoice-no {
          font-family: 'SF Mono', 'Consolas', monospace;
          font-weight: 600;
          color: var(--primary);
        }

        .period-cell {
          white-space: nowrap;
        }

        .period-range {
          font-size: 0.8125rem;
          color: var(--text-secondary);
        }

        .amount-cell {
          font-weight: 600;
          text-align: right;
        }

        .amount-cell.pkr {
          color: var(--text-muted);
          font-size: 0.8125rem;
        }

        .invoice-status {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: capitalize;
        }

        .invoice-status.status-pending {
          background: rgba(245, 158, 11, 0.15);
          color: #f59e0b;
        }

        .invoice-status.status-paid {
          background: rgba(34, 197, 94, 0.15);
          color: #22c55e;
        }

        .invoice-status.status-overdue {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }

        .invoice-status.status-partial {
          background: rgba(59, 130, 246, 0.15);
          color: #3b82f6;
        }

        .invoice-status.status-cancelled {
          background: rgba(100, 116, 139, 0.15);
          color: #64748b;
        }

        .status-icon.paid { color: #22c55e; }
        .status-icon.pending { color: #f59e0b; }
        .status-icon.overdue { color: #ef4444; }
        .status-icon.partial { color: #3b82f6; }

        .download-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.25);
          border-radius: 6px;
          color: #3b82f6;
          font-size: 0.8125rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .download-btn:hover:not(:disabled) {
          background: rgba(59, 130, 246, 0.2);
          border-color: rgba(59, 130, 246, 0.4);
        }

        .download-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-spinner {
          width: 14px;
          height: 14px;
          border: 2px solid rgba(59, 130, 246, 0.3);
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .text-center {
          text-align: center;
        }
      `}</style>
    </div>
  )
}
